import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, AlertCircle, CheckCircle } from 'lucide-react';
import '../App.css';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [queueHistory, setQueueHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Generate mock data for demonstration
    const mockStats = {
      current_queue_length: 5,
      avg_queue_length: 4.2,
      max_queue_length: 12,
      operator_active_percentage: 85.5,
      total_frames_processed: 1250,
      avg_wait_time: 8.5,
      peak_hour: '14:00',
    };

    const mockQueueHistory = Array.from({ length: 24 }, (_, i) => ({
      time: `${i}:00`,
      queue: Math.floor(Math.random() * 15) + 2,
      operator_active: Math.floor(Math.random() * 100)
    }));

    const mockAlerts = [
      { id: 1, type: 'warning', message: 'Queue length exceeds 10 people', timestamp: new Date().toLocaleTimeString() },
      { id: 2, type: 'info', message: 'Operator activity detected', timestamp: new Date().toLocaleTimeString() },
    ];

    setStats(mockStats);
    setQueueHistory(mockQueueHistory);
    setAlerts(mockAlerts);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-cyan-400 rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Live Dashboard</h1>
        <div className="text-sm text-gray-400">Last updated: {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card success">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Current Queue</p>
              <p className="text-3xl font-bold">{stats?.current_queue_length}</p>
              <p className="text-xs opacity-75 mt-2">people</p>
            </div>
            <Users className="w-10 h-10 opacity-50" />
          </div>
        </div>

        <div className="stat-card alt">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Avg Queue Length</p>
              <p className="text-3xl font-bold">{stats?.avg_queue_length.toFixed(1)}</p>
              <p className="text-xs opacity-75 mt-2">24h average</p>
            </div>
            <TrendingUp className="w-10 h-10 opacity-50" />
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Operator Activity</p>
              <p className="text-3xl font-bold">{stats?.operator_active_percentage.toFixed(1)}%</p>
              <p className="text-xs opacity-75 mt-2">active time</p>
            </div>
            <CheckCircle className="w-10 h-10 opacity-50" />
          </div>
        </div>

        <div className="stat-card warning">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Max Queue</p>
              <p className="text-3xl font-bold">{stats?.max_queue_length}</p>
              <p className="text-xs opacity-75 mt-2">peak today</p>
            </div>
            <AlertCircle className="w-10 h-10 opacity-50" />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Queue History */}
        <div className="glass-panel p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Queue Length (24h)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={queueHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ background: '#1e293b', border: '1px solid #475569' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="queue" 
                stroke="#22d3ee" 
                dot={false}
                name="Queue Length"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Operator Activity */}
        <div className="glass-panel p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Operator Activity (24h)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={queueHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ background: '#1e293b', border: '1px solid #475569' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend />
              <Bar 
                dataKey="operator_active" 
                fill="#a855f7"
                name="Activity %"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts */}
      <div className="glass-panel p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Alerts</h3>
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${
              alert.type === 'warning' 
                ? 'bg-yellow-500 bg-opacity-10 border-yellow-500' 
                : 'bg-blue-500 bg-opacity-10 border-blue-500'
            }`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-white">{alert.message}</p>
                <span className="text-xs text-gray-400">{alert.timestamp}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Stats */}
      <div className="glass-panel p-6">
        <h3 className="text-lg font-semibold text-white mb-4">System Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-400 text-sm mb-2">Frames Processed</p>
            <p className="text-2xl font-bold text-cyan-400">{stats?.total_frames_processed}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm mb-2">Avg Wait Time</p>
            <p className="text-2xl font-bold text-cyan-400">{stats?.avg_wait_time}m</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm mb-2">Peak Hour</p>
            <p className="text-2xl font-bold text-cyan-400">{stats?.peak_hour}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm mb-2">Status</p>
            <p className="text-2xl font-bold text-green-400">Online</p>
          </div>
        </div>
      </div>
    </div>
  );
}
